import { isAtticScopeMeasured, isCellarScopeMeasured } from 'models/purchaseFlow/measurementDTO'
import { useEffect, useMemo, useRef } from 'react'
import { useGetMeasurementQuote } from 'dataQueries/purchase.query'

import { MeasurementQuoteRequestDTO } from 'models/purchaseFlow/measurementQuoteDTO'
import { Nullable } from 'models/helpers'
import { ProductKind } from 'constants/product'
import { findHintQuoteDivergences } from 'components/common/MeasurementControls/measurementHintPricing'
import { logger } from 'utils/logging'
import { useDebouncedMemo } from 'utils/helpers'
import { useMeasurementRateCard } from 'components/pages/PurchaseFlow/steps/MeasurementStep/useMeasurementRateCard'
import { useMeasurements } from 'components/pages/PurchaseFlow/steps/MeasurementStep/MeasurementStep.context'
import { usePurchaseFlowProducts, useTargetOrderUser } from 'components/pages/PurchaseFlow/_main/contexts'

/** Debounce applied to the measurement configuration before requesting a fresh quote */
const QUOTE_DEBOUNCE_MS = 500

/**
 * Debounced measurement quote for the On-Site Area Report redesign (ISSUE-2188).
 * Active while a measurement product is in the cart and a surface area is entered. Shared by the cost-assumption panel and the cart totals —
 * both consumers resolve to the same react-query key, so only one request is in flight.
 */
export function useMeasurementCostAssumption() {
  const { selectedProducts } = usePurchaseFlowProducts()
  const { adminSelectedUserEmail } = useTargetOrderUser()

  const {
    isUnitBasedPropertyType,
    surfaceArea,
    numberOfFloors,
    cellarScope,
    atticScope,
    residentialUnits,
    measurementPropertyType,
    isMeasurementAddOnsEnabled,
    isExpressServiceOffered,
    isAdditionalHouseViewsOffered,
    isExpressServiceSelected,
    isAdditionalHouseViewsSelected
  } = useMeasurements()

  const measurementProduct = useMemo(
    () => Object.values(selectedProducts).find(product => product.kind === ProductKind.MEASUREMENT_ON_SITE),
    [selectedProducts]
  )

  const quotePayload = useDebouncedMemo<Nullable<MeasurementQuoteRequestDTO>>(() => {
    if (!measurementProduct) return null
    if (isUnitBasedPropertyType && residentialUnits.length === 0) return null

    const totalFloors = isUnitBasedPropertyType
      ? residentialUnits.reduce((sum, unitFloors) => sum + unitFloors, 0)
      : numberOfFloors

    return {
      propertyType: measurementPropertyType,
      surfaceArea: Number(surfaceArea),
      numberOfFloors: totalFloors,
      basementAreaToMeasure: isCellarScopeMeasured(cellarScope),
      atticAreaToMeasure: isAtticScopeMeasured(atticScope),
      cellarScope,
      atticScope,
      // Add-ons (ISSUE-2838) — off/unpriced drops them from the payload entirely, keeping the pre-add-on contract
      ...(isMeasurementAddOnsEnabled
        ? {
          expressService: isExpressServiceOffered && isExpressServiceSelected,
          additionalHouseViews: isAdditionalHouseViewsOffered && isAdditionalHouseViewsSelected
        }
        : {}),
      ...(isUnitBasedPropertyType ? { numberOfResidentialUnits: residentialUnits.length } : {})
    }
  }, [
    atticScope,
    cellarScope,
    isAdditionalHouseViewsOffered,
    isAdditionalHouseViewsSelected,
    isExpressServiceOffered,
    isExpressServiceSelected,
    isMeasurementAddOnsEnabled,
    isUnitBasedPropertyType,
    measurementProduct,
    measurementPropertyType,
    numberOfFloors,
    residentialUnits,
    surfaceArea
  ], QUOTE_DEBOUNCE_MS)

  // Admins quote on behalf of a selected client — the admin endpoint variant requires that client's
  // email (empty for clients, so their request stays param-free)
  const quoteQuery = useGetMeasurementQuote(quotePayload, adminSelectedUserEmail || undefined)

  // Same react-query key as the hint controller's rate card, so this adds no request
  const pricingQuery = useMeasurementRateCard()

  // Tripwire (ISSUE-3007): the hint rules mirror MeasurementQuoteService by hand and have silently
  // drifted twice (ISSUE-2861 before) — each time a pill promised an amount the engine billed
  // differently, caught only by manual QA. The quote in hand carries the engine's own unit prices,
  // so a divergence becomes a Sentry warning instead of a client-facing price promise.
  const reportedDivergences = useRef(new Set<string>())
  useEffect(() => {
    const quote = quoteQuery.data?.data
    const pricing = pricingQuery.data?.data
    const { propertyType, numberOfFloors, cellarScope: quotedCellarScope, atticScope: quotedAtticScope } = quotePayload ?? {}
    // placeholderData is the previous configuration's quote — only compare a settled response against
    // the payload that produced it
    if (!quote || !pricing || !propertyType || !numberOfFloors || !quotedCellarScope || !quotedAtticScope) return
    if (quoteQuery.isError || quoteQuery.isFetching || quoteQuery.isPlaceholderData) return

    const levelConfig = { propertyType, totalFloors: numberOfFloors, cellarScope: quotedCellarScope }
    // From the payload that produced this quote, so a kept-previous response is never compared against a since-changed selection
    const quotedAddOns = { expressService: quotePayload?.expressService, additionalHouseViews: quotePayload?.additionalHouseViews }

    findHintQuoteDivergences(levelConfig, pricing, quote.lineItems, quotedAtticScope, quotedAddOns).forEach(divergence => {
      const signature = `${divergence.lineItemType}:${propertyType}:${numberOfFloors}:${divergence.hintAmount}:${divergence.quoteAmount}`
      if (reportedDivergences.current.has(signature)) return

      reportedDivergences.current.add(signature)
      logger.warn('Measurement hint rate diverged from the engine quote line — measurementHintPricing.ts no longer mirrors MeasurementQuoteService', undefined, {
        context: {
          component: 'useMeasurementCostAssumption',
          metadata: {
            lineItemType: divergence.lineItemType,
            hintAmount: divergence.hintAmount,
            quoteAmount: divergence.quoteAmount,
            propertyType,
            totalFloors: numberOfFloors,
            cellarScope: quotedCellarScope,
            atticScope: quotedAtticScope
          }
        }
      })
    })
  }, [quotePayload, quoteQuery.data, quoteQuery.isError, quoteQuery.isFetching, quoteQuery.isPlaceholderData, pricingQuery.data])

  // Recomputed only when a fresh quote lands, so a kept-previous amount is never labelled with a newer configuration
  const quotedLabelInputs = useMemo(() => ({
    isPricedPerResidentialUnit: quotePayload?.numberOfResidentialUnits != null,
    measurementPropertyType: quotePayload?.propertyType ?? measurementPropertyType,
    cellarScope: quotePayload?.cellarScope ?? cellarScope,
    atticScope: quotePayload?.atticScope ?? atticScope
    // The dependency is intentionally the response object alone, not the live inputs — see above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [quoteQuery.data])

  return {
    /** Whether the quote-driven pricing applies (measurement product selected, surface area entered) */
    isActive: !!measurementProduct && !!quotePayload && quotePayload.surfaceArea > 0,
    /** Catalog id of the selected measurement product, e.g. to hide its legacy cart line */
    measurementProductId: measurementProduct?.id ?? null,
    /** The latest quote breakdown, undefined until the first response — a failed request never surfaces the stale previous quote as current */
    quote: quoteQuery.isError ? undefined : quoteQuery.data?.data,
    /** Whether a quote request is in flight */
    isFetching: quoteQuery.isFetching,
    /** Whether the latest quote request failed — the panel explains instead of showing (stale) line items */
    isError: quoteQuery.isError,
    /**
     * Whether the rendered quote was actually priced per residential unit — i.e. the request that produced it
     * carried a unit count. Captured together with the response (quotedLabelInputs), so the panel never labels
     * a base line "First residential unit" for a quote the engine priced some other way.
     */
    isPricedPerResidentialUnit: quotedLabelInputs.isPricedPerResidentialUnit,
    /** Property type of the rendered quote, for localised line labels */
    measurementPropertyType: quotedLabelInputs.measurementPropertyType,
    /** Cellar scope of the rendered quote, for localised CELLAR line labels (single room / present-not-measured price at €0) */
    cellarScope: quotedLabelInputs.cellarScope,
    /** Attic scope of the rendered quote, for localised ATTIC line labels */
    atticScope: quotedLabelInputs.atticScope
  }
}
